import { Auth } from 'aws-amplify';
import { withTimeout } from '@/utils/with-timeout';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';

/**
 * Custom Cognito attribute that maps to an employee record in the database.
 */
export const EMPLOYEE_UUID_ATTRIBUTE = 'custom:employee_uuid';

/**
 * Cognito calls can hang forever when the endpoint is unreachable, so don't
 * wait on them longer than this.
 */
export const AUTH_NETWORK_TIMEOUT_MS = 15000;

/**
 * If we refreshed this recently, a new failure is just a straggler that went
 * out with the old token, so hand it the fresh one instead of refreshing again.
 */
const REFRESH_REUSE_WINDOW_MS = 30000;

/**
 * Service for managing authentication sessions via AWS Cognito.
 */
class SessionServiceImpl {
  private inFlightRefresh: Promise<string> | null = null;
  private lastRefreshSucceededAt = 0;

  /**
   * Get just the ID token JWT string.
   * Convenience method for API calls.
   *
   * @returns The current ID token.
   * @throws If there is no signed-in user or the session cannot be refreshed.
   */
  async getIdToken(): Promise<string> {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  }

  /**
   * Refresh the ID token over the network even when Amplify thinks the cached
   * one is still valid (it's wrong under device clock skew). Concurrent callers
   * share a single refresh, and nobody waits past AUTH_NETWORK_TIMEOUT_MS.
   *
   * @returns A valid ID token.
   * @throws If the refresh doesn't settle within {@link AUTH_NETWORK_TIMEOUT_MS}.
   */
  refreshIdTokenOnce(): Promise<string> {
    const sinceLastRefresh = Date.now() - this.lastRefreshSucceededAt;
    if (sinceLastRefresh >= 0 && sinceLastRefresh < REFRESH_REUSE_WINDOW_MS) {
      return withTimeout(
        this.getIdToken(),
        AUTH_NETWORK_TIMEOUT_MS,
        'Token refresh timed out',
      );
    }

    if (!this.inFlightRefresh) {
      const refresh = this.forceRefreshIdToken();
      this.inFlightRefresh = refresh;

      refresh
        .then(() => {
          this.lastRefreshSucceededAt = Date.now();
        })
        // Ensure late failures don't become an unhandled rejection.
        .catch(() => undefined);

      withTimeout(refresh, AUTH_NETWORK_TIMEOUT_MS)
        .catch(() => undefined)
        .finally(() => {
          if (this.inFlightRefresh === refresh) {
            this.inFlightRefresh = null;
          }
        });
    }

    // Wrap per caller so everyone gets a full timeout from when they joined,
    // not the creator's partly-elapsed one.
    return withTimeout(
      this.inFlightRefresh,
      AUTH_NETWORK_TIMEOUT_MS,
      'Token refresh timed out',
    );
  }

  /**
   * Forces an unconditional refreshSession round trip.
   *
   * @returns The newly issued ID token.
   * @throws If there is no current session, or Cognito rejects the refresh.
   */
  private async forceRefreshIdToken(): Promise<string> {
    const user = await Auth.currentAuthenticatedUser();
    // Use getSignInUserSession since Auth.currentSession() would kick off
    // a second refresh
    const session: CognitoUserSession | null = user.getSignInUserSession();
    if (!session) {
      throw new Error('No current session to refresh');
    }

    return new Promise<string>((resolve, reject) => {
      user.refreshSession(
        session.getRefreshToken(),
        (err: unknown, newSession: CognitoUserSession) => {
          if (err || !newSession) {
            reject(err ?? new Error('Failed to refresh Cognito session'));
            return;
          }

          resolve(newSession.getIdToken().getJwtToken());
        },
      );
    });
  }

  /**
   * Get the employee UUID from the authenticated user's custom attribute.
   * This maps the Cognito user to an employee record in the database.
   *
   * @returns The employee UUID stored on the user's custom attribute.
   * @throws If there is no authenticated user.
   */
  async getEmployeeUUID(): Promise<string> {
    const user = await Auth.currentAuthenticatedUser();
    return user.attributes[EMPLOYEE_UUID_ATTRIBUTE];
  }

  /**
   * Get the current session if authenticated.
   * Used to verify authentication after sign-in.
   *
   * @returns The current Cognito session.
   * @throws Error if not authenticated
   */
  async getCurrentSession() {
    return Auth.currentSession();
  }
}

/**
 * Singleton instance for use across the application.
 */
export const SessionService = new SessionServiceImpl();
